// Which wb server this app talks to. The app is a static bundle that can be served
// from anywhere (http-server, Pages, a CDN), so the backend is configuration, not
// "whatever host served index.html". In order of precedence:
//
//   ?wb=<base>          saved for this browser, then dropped from the URL
//                       (?wb= or ?wb=default clears it)
//   localStorage        the last choice made with ?wb= or the picker
//   config.json         deployed next to index.html:
//                       { default, backends, allowDomains, appDomains }
//   ''                  same origin as the page (the Vite / Pages proxy setups)
//
// A base is only accepted if it is one of config.backends or its hostname is
// within config.allowDomains, so a crafted ?wb= link cannot point the login form
// at someone else's server.
//
// wb resolves the tenant from its own Host header, so the base gets the tenant
// prefixed: a page on dave.<appDomain> (dave.cellbie.local:8000) talks to
// https://dave.<base host> (https://dave.cellbie2.dev). On a host outside
// config.appDomains (cellbie.app on Cloudflare Pages, which can't serve a
// wildcard domain) the tenant comes from ?d=<domain> instead, saved in
// localStorage like ?wb= (?d= clears it); a redirect rule sends
// dave.cellbie.app/<path> to cellbie.app/<path>?d=dave. With a backend but no
// tenant, the app asks for one rather than calling the bare backend host.

const STORAGE_KEY = 'wb.backend';
const TENANT_KEY = 'wb.tenant';
const TENANT_RE = /^[a-z0-9][a-z0-9_-]{0,62}$/;
// wb blocks the caller's IP when a request names an unknown tenant with one of
// these prefixes (SUSPECT_DOMAIN_PREFIXES in wb's modules/wbutils.py), so never
// send one: www.cellbie.app would otherwise redirect to ?d=www and get the user
// blocked
const SUSPECT_TENANTS = ['www', 'm', 'store', 'shop', 'us', 'airflow', 'airflow-prod', 'cloud', 'blog',
  'gateway', 'pulse', 'vpn', 'intern', 'mdm', 'ssl', 'mail', 'yourdomain', 'ms', 'wildcard', 'secure'];
// served by this bundle, never by wb
const APP_PREFIXES = ['static/', 'vendor/', 'app/', 'assets/', 'src/', 'config.json'];
const ABSOLUTE = /^([a-z][a-z0-9+.-]*:|\/\/)/i;

export const backend = {
  base: '', // the chosen backend, before the tenant is prefixed
  tenant: '', // from the page host (config.appDomains) or ?d=; '' = none
  tenantFromHost: false,
  origin: '', // where requests go; '' = same origin as the page
  config: { default: '', backends: [], allowDomains: [], appDomains: [] },
};

function tenantOf(hostname) {
  for (const d of backend.config.appDomains || []) {
    if (hostname.endsWith('.' + d)) return hostname.slice(0, -(d.length + 1));
  }
  return '';
}

export function cleanTenant(value) {
  const t = (value || '').trim().toLowerCase();
  return TENANT_RE.test(t) && !SUSPECT_TENANTS.includes(t) ? t : null;
}

function readTenant() {
  try {
    return cleanTenant(localStorage.getItem(TENANT_KEY));
  } catch (e) {
    return null;
  }
}

function storeTenant(tenant) {
  try {
    if (tenant) localStorage.setItem(TENANT_KEY, tenant);
    else localStorage.removeItem(TENANT_KEY);
  } catch (e) {
    /* private mode: the choice lasts for this page only */
  }
}

function withTenant(base) {
  if (!base || !backend.tenant) return base;
  const u = new URL(base);
  return u.protocol + '//' + backend.tenant + '.' + u.host;
}

function normalize(value) {
  if (!value) return '';
  try {
    const u = new URL(value.includes('://') ? value : 'https://' + value);
    if (u.protocol !== 'https:' && u.protocol !== 'http:') return null;
    return u.origin;
  } catch (e) {
    return null;
  }
}

function isAllowed(origin) {
  if (origin === '') return true;
  if (!origin) return false;
  const cfg = backend.config;
  if ((cfg.backends || []).some((b) => normalize(b.url) === origin)) return true;
  const host = new URL(origin).hostname;
  return (cfg.allowDomains || []).some((d) => host === d || host.endsWith('.' + d));
}

function readStored() {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch (e) {
    return null;
  }
}

function store(origin) {
  try {
    if (origin === null) localStorage.removeItem(STORAGE_KEY);
    else localStorage.setItem(STORAGE_KEY, origin);
  } catch (e) {
    /* private mode: the choice lasts for this page only */
  }
}

async function loadConfig() {
  try {
    const resp = await fetch('config.json', { cache: 'no-cache' });
    if (resp.ok) Object.assign(backend.config, await resp.json());
  } catch (e) {
    /* no config.json: same origin */
  }
}

// ?wb= and ?d= on the current URL: apply them, then remove them so they do not
// linger in bookmarks or get shared along with a tab link
function takeQueryOverrides() {
  const url = new URL(window.location.href);
  const hasWb = url.searchParams.has('wb');
  const hasD = url.searchParams.has('d');
  if (!hasWb && !hasD) return;
  const wb = url.searchParams.get('wb');
  const d = url.searchParams.get('d');
  url.searchParams.delete('wb');
  url.searchParams.delete('d');
  window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash);
  if (hasWb) {
    if (wb === '' || wb === 'default') store(null);
    else {
      const origin = normalize(wb);
      if (origin !== null && isAllowed(origin)) store(origin);
      else console.warn('Ignoring ?wb=' + wb + ': not in config.json backends or allowDomains');
    }
  }
  if (hasD) {
    if (d === '') storeTenant(null);
    else if (cleanTenant(d)) storeTenant(cleanTenant(d));
    else console.warn('Ignoring ?d=' + d + ': not a usable domain name');
  }
}

export async function loadBackend() {
  await loadConfig();
  takeQueryOverrides();
  const stored = readStored();
  const storedOrigin = stored === null ? null : normalize(stored);
  if (storedOrigin !== null && isAllowed(storedOrigin)) backend.base = storedOrigin;
  else backend.base = normalize(backend.config.default) || '';
  const hostTenant = cleanTenant(tenantOf(window.location.hostname));
  backend.tenantFromHost = !!hostTenant;
  backend.tenant = hostTenant || readTenant() || '';
  backend.origin = withTenant(backend.base);
  publish();
  return backend;
}

// Where a request for `path` should go. Absolute URLs and this bundle's own files
// are left alone; everything else is a wb path.
export function apiUrl(path) {
  if (!backend.origin || !path || ABSOLUTE.test(path)) return path;
  const rel = path.replace(/^\//, '');
  if (APP_PREFIXES.some((p) => rel.startsWith(p))) return path;
  return backend.origin + '/' + rel;
}

// A backend is configured but no tenant is known: the app must ask for one
// instead of calling the bare backend host, which serves no tenant.
export function needsTenant() {
  return !!backend.base && !backend.tenant;
}

// Pick a tenant from the UI (or null to forget it): saved for this browser, then
// a fresh document, as for chooseBackend.
export function chooseTenant(value) {
  const t = value === null ? null : cleanTenant(value);
  if (value !== null && !t) return false;
  storeTenant(t);
  clearSessionCaches();
  window.location.replace('/');
  return true;
}

function clearSessionCaches() {
  try {
    Object.keys(sessionStorage)
      .filter((k) => k.startsWith('wb.'))
      .forEach((k) => sessionStorage.removeItem(k));
  } catch (e) {
    /* ignore */
  }
}

export function isCrossOrigin() {
  return !!backend.origin && backend.origin !== window.location.origin;
}

// Session token. Cross-site, browsers drop (Safari) or partition (Firefox) wb's
// session cookie, so the session id travels in an X-WB-Session header instead:
// "new" until wb hands one out, and whatever id wb returns in the same response
// header from then on (it changes at login, logout and session expiry). Kept per
// backend origin in localStorage, so tabs share a login the way the cookie did.
// Same-origin setups keep using the cookie and never send the header.
const SESSION_HEADER = 'X-WB-Session';
const sessionKey = () => 'wb.session:' + backend.origin;

export function sessionHeaders() {
  if (!isCrossOrigin()) return {};
  let token = null;
  try {
    token = localStorage.getItem(sessionKey());
  } catch (e) {
    /* storage unavailable: every page load starts a new session */
  }
  return { [SESSION_HEADER]: token || 'new' };
}

// getHeader: (name) => value, from a fetch Response or a jqXHR
export function captureSession(getHeader) {
  if (!isCrossOrigin()) return;
  const token = getHeader(SESSION_HEADER);
  if (!token) return;
  try {
    localStorage.setItem(sessionKey(), token);
  } catch (e) {
    /* ignore */
  }
}

// fetch() against the backend: base URL, token header, and the new session id
// picked up from the response. Cross-origin the token is the whole session, so
// cookies are left out entirely (credentials: 'omit'); same origin uses the cookie.
export async function apiFetch(path, init = {}) {
  const headers = Object.assign({}, sessionHeaders(), init.headers);
  const credentials = isCrossOrigin() ? 'omit' : 'same-origin';
  const resp = await fetch(apiUrl(path), Object.assign({ credentials }, init, { headers }));
  captureSession((name) => resp.headers.get(name));
  return resp;
}

// Pick a backend from the UI: saved for this browser, then a fresh document so
// nothing from the old server's session survives.
export function chooseBackend(origin) {
  const norm = normalize(origin);
  if (norm === null || !isAllowed(norm)) return;
  store(norm === normalize(backend.config.default) ? null : norm);
  clearSessionCaches();
  window.location.replace('/');
}

// Every legacy $.get/$.post/$.ajax/.load goes through here: relative wb paths get
// the backend origin and the session token, without cookies (XHR's withCredentials
// stays false). jQuery decides crossDomain before prefilters run, so it is set here too.
function installAjaxPrefilter() {
  const $ = window.jQuery;
  if (!$ || !$.ajaxPrefilter) return;
  $.ajaxPrefilter((options, originalOptions, jqXHR) => {
    if (!isCrossOrigin()) return;
    const url = apiUrl(options.url);
    if (url === options.url) return;
    options.url = url;
    options.crossDomain = true;
    options.headers = Object.assign({}, options.headers, sessionHeaders());
    jqXHR.always(() => captureSession((name) => jqXHR.getResponseHeader(name)));
  });
}

// The tenant stylesheets in index.html carry data-wb-href instead of href, so they
// load from the backend once it is known.
function loadTenantStyles() {
  document.querySelectorAll('link[data-wb-href]').forEach((link) => {
    link.href = apiUrl(link.getAttribute('data-wb-href'));
  });
}

function publish() {
  const WB = (window.WB = window.WB || {});
  WB.backend = backend;
  WB.apiUrl = apiUrl;
  WB.apiFetch = apiFetch;
  installAjaxPrefilter();
  // no tenant yet: the bare backend host serves no tenant's stylesheets
  if (!needsTenant()) loadTenantStyles();
}

// The list the picker offers: config.backends, plus the current base if it came
// from ?wb= and is not in the list.
export function backendChoices() {
  const list = (backend.config.backends || []).map((b) => ({ label: b.label || b.url, origin: normalize(b.url) || '' }));
  if (!list.some((b) => b.origin === backend.base)) {
    list.push({ label: backend.base || window.location.origin, origin: backend.base });
  }
  return list;
}
